import { InfoToolTip } from '@commenda/icons';
import {
  AddressInput,
  Alert,
  Button,
  Checkbox,
  CountryInput,
  DatePicker,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Icons,
  iconVariants,
  Input,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleFileUploader,
  Tooltip,
} from '@commenda/kakashi';
import { defaultAddressSchema } from '@commenda/schema/address';
import { IAddress } from '@commenda/types';
import { FileBasic } from '@commenda/types/src/file/types';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { EducationalQualificationEnum } from '@commenda/types/src/random';
import {
  classNames,
  cn,
  documentName,
  EDUCATIONAL_QUALIFICATION_TYPE_ENUM_TO_TEXT,
  fetcher,
} from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Country, LegacyDocumentType } from '@prisma/client';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  checkIndividualEmailAgainstCompanyEmails,
  getStatesDropdown,
} from '../../../common/helpers';
import { DirectorFormType } from '../../Director/DirectorFormType';
import { getIdentityProof } from '../utils';
import { directorSchema } from './schema';

const DirectorForm: DirectorFormType<DirectorBase> = ({
  director,
  companyDataMutate,
  setOpen,
  isAddingDirector,
  setIsAddingDirector,
  companyId,
  corporationId,
  token,
  setNotification,
  onErrorResolve,
  selectedDirectorIds,
  setSelectedDirectorIds,
  modalConfig,
  workRequestData,
  allCorporations,
}) => {
  const identityProofFile = getIdentityProof(director, corporationId);

  const passportSizePhotoFile = director?.directorFiles
    ?.sort((a, b) => b.id - a.id)
    .find(
      (eachFile) =>
        eachFile.documentType === LegacyDocumentType.PassportSizePhoto
    );

  const specimenSignatureFile = director?.directorFiles
    ?.sort((a, b) => b.id - a.id)
    .find(
      (eachFile) =>
        eachFile.documentType === LegacyDocumentType.SpecimenSignature
    );

  const [addressProof, setAddressProof] = useState(director?.addressProof);
  const [aadharCard, setAadharCard] = useState(director?.aadharCard);
  const [panCard, setPanCard] = useState(director?.panCard);
  const [identityProof, setIdentityProof] =
    useState<FileBasic>(identityProofFile);
  const [specimenSignature, setSpecimenSignature] = useState<FileBasic>(
    specimenSignatureFile
  );
  const [passportSizePhoto, setPassportSizePhoto] = useState<FileBasic>(
    passportSizePhotoFile
  );
  const [identityProofTypeForIndia, setIdentityProofTypeForIndia] =
    useState<LegacyDocumentType>(identityProofFile?.documentType);

  const identityProofDocumentTypes: LegacyDocumentType[] = [
    LegacyDocumentType.DrivingLicense,
    LegacyDocumentType.Passport,
    LegacyDocumentType.VoterId,
  ];
  type DirectorSchemaFields = z.infer<typeof directorSchema>;
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm<DirectorSchemaFields>({
    resolver: zodResolver(directorSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      directorFirstName: director?.directorFirstName,
      directorLastName: director?.directorLastName,
      email: director?.email,
      number: director?.number,
      personalDetails: {
        nationality: director?.personalDetails?.nationality,
        educationalQualification:
          director?.personalDetails?.educationalQualification,
        hasDSC: director?.personalDetails?.hasDSC || false,
        occupation: director?.personalDetails?.occupation,
        placeOfBirth: director?.personalDetails?.placeOfBirth,
        dateOfOccupancyInCurrentAddress:
          director?.personalDetails?.dateOfOccupancyInCurrentAddress,
      },
      addressProof: {
        id: director?.addressProof?.id || -1,
        documentType: LegacyDocumentType.AddressProof,
        fileName: director?.addressProof?.fileName,
        fileUrl: director?.addressProof?.fileUrl,
      },
      operatingAddress: director?.operatingAddress || defaultAddressSchema,
      hasDIN: !!director?.directorIdentificationNumber,
      directorIdentificationNumber:
        director?.directorIdentificationNumber || '',
    },
  });

  const onSubmit = async (data: DirectorSchemaFields) => {
    // Business logic validation - check email uniqueness
    const emailToCheck = data.email?.trim().toLowerCase();
    if (emailToCheck) {
      // Only run workRequestData validations if workRequestData is available (for new directors)
      if (workRequestData) {
        const companyEmail = workRequestData?.corporation?.contactEmail
          ?.trim()
          .toLowerCase();
        const existingDirectorEmails =
          workRequestData?.directors
            ?.map((d) => d.email?.trim().toLowerCase())
            .filter(Boolean) || [];
        const currentDirectorEmail = director?.email?.trim().toLowerCase();

        // Check against company email
        if (companyEmail && emailToCheck === companyEmail) {
          setError('email', {
            type: 'manual',
            message: 'Director email cannot be the same as company email.',
          });
          setIsAddingDirector(false);
          return;
        }

        // Check against other director emails (excluding current director's original email)
        const otherDirectorEmails = existingDirectorEmails.filter(
          (email) => email !== currentDirectorEmail
        );
        if (otherDirectorEmails.includes(emailToCheck)) {
          setError('email', {
            type: 'manual',
            message: 'This email is already used by another director.',
          });
          setIsAddingDirector(false);
          return;
        }
      }

      // Check if director email matches any company email across all entities
      if (allCorporations?.length) {
        const companyEmailValidation = checkIndividualEmailAgainstCompanyEmails(
          emailToCheck,
          allCorporations
        );

        if (!companyEmailValidation.isUnique) {
          setError('email', {
            type: 'manual',
            message: `This email is already used as a company email by another entity. Individual emails must be different from all company emails.`,
          });
          setIsAddingDirector(false);
          return;
        }
      }
    }

    setIsAddingDirector(true);

    const documentTypeForIdentityProof =
      data.personalDetails?.nationality === Country.IN
        ? identityProofTypeForIndia
        : LegacyDocumentType.Passport;

    const workRequestId = workRequestData?.id;

    const res = await fetcher(
      director?.id ? `director/${director.id}` : 'director',
      {
        method: 'POST',
        body: {
          directorFirstName: data.directorFirstName,
          directorLastName: data.directorLastName,
          number: data.number,
          email: data.email,
          personalDetails: {
            nationality: data?.personalDetails?.nationality,
            educationalQualification:
              data?.personalDetails?.educationalQualification,
            hasDSC: data?.personalDetails?.hasDSC,
            occupation: data.personalDetails.occupation,
            placeOfBirth: data.personalDetails.placeOfBirth,
            dateOfOccupancyInCurrentAddress:
              data.personalDetails.dateOfOccupancyInCurrentAddress,
          },
          operatingAddress: data.operatingAddress,
          filesToAdd: [
            {
              ...data.identityProof,
              documentType: documentTypeForIdentityProof,
            },
            {
              ...data.specimenSignature,
              documentType: LegacyDocumentType.SpecimenSignature,
            },
            {
              ...data.passportSizePhoto,
              documentType: LegacyDocumentType.PassportSizePhoto,
            },
          ],
          corporationHasDirector: [
            {
              corporationId,
              additionalDirectorInformation: {
                ...(modalConfig?.isDirector ? { isDirector: true } : {}),
                ...(modalConfig?.isAuthorizedSignatory
                  ? { isAuthorizedSignatory: true }
                  : {}),
              },
              incorporationInfo: {
                identityProofTypeUsedByDirector:
                  data.personalDetails?.nationality === Country.IN
                    ? identityProofTypeForIndia
                    : LegacyDocumentType.Passport,
              },
            },
          ],
          addressProof,
          aadharCard,
          panCard,
          hasDIN: data.hasDIN,
          directorIdentificationNumber: data.directorIdentificationNumber,
          companyId,
          ...(workRequestId != null ? { wrkReqId: workRequestId } : {}),
        },
      },
      token
    );

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again later or contact team Commenda if you need help',
      });

      await companyDataMutate();
      setIsAddingDirector(false);
      return;
    }

    if (res.status === 409) {
      const error = await res.json();
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: error.message,
      });
      setIsAddingDirector(false);
      return;
    }
    await companyDataMutate();
    const resData = await res.json();
    if (setSelectedDirectorIds) {
      setSelectedDirectorIds([...(selectedDirectorIds || []), resData.id]);
    }
    if (modalConfig?.postSave) {
      modalConfig.postSave({
        id: resData.id,
        directorFirstName: resData.directorFirstName,
        directorLastName: resData.directorLastName,
      });
    }
    if (onErrorResolve) onErrorResolve();
    setIsAddingDirector(false);
    reset();
    setOpen(false);
  };

  useEffect(() => {
    if (addressProof) {
      setValue('addressProof', {
        ...addressProof,
        id: addressProof?.id,
      });
      clearErrors('addressProof');
    } else {
      setValue('addressProof', null);
    }
  }, [addressProof]);

  useEffect(() => {
    if (aadharCard) {
      setValue('aadharCard', {
        ...aadharCard,
        id: aadharCard?.id,
      });
      clearErrors('aadharCard');
    } else {
      setValue('aadharCard', null);
    }
  }, [aadharCard]);

  useEffect(() => {
    if (panCard) {
      setValue('panCard', {
        ...panCard,
        id: panCard?.id,
      });
      clearErrors('panCard');
    } else {
      setValue('panCard', null);
    }
  }, [panCard]);

  useEffect(() => {
    if (identityProof) {
      setValue('identityProof', {
        fileName: identityProof.fileName,
        fileUrl: identityProof.fileUrl,
        id: identityProof?.id,
      });
      clearErrors('identityProof');
    } else {
      setValue('identityProof', null);
    }
  }, [identityProof]);

  useEffect(() => {
    if (specimenSignature) {
      setValue('specimenSignature', {
        fileName: specimenSignature.fileName,
        fileUrl: specimenSignature.fileUrl,
        id: specimenSignature?.id,
      });
      clearErrors('specimenSignature');
    } else {
      setValue('specimenSignature', null);
    }
  }, [specimenSignature]);

  useEffect(() => {
    if (passportSizePhoto) {
      setValue('passportSizePhoto', {
        fileName: passportSizePhoto.fileName,
        fileUrl: passportSizePhoto.fileUrl,
        id: passportSizePhoto?.id,
      });
      clearErrors('passportSizePhoto');
    } else {
      setValue('passportSizePhoto', null);
    }
  }, [passportSizePhoto]);

  useEffect(() => {
    if (watch('personalDetails.nationality') === Country.IN) {
      if (!identityProofTypeForIndia) {
        setIdentityProof(null);
        return;
      }
      const newIdentityProofFile = director?.directorFiles
        ?.sort((a, b) => b.id - a.id)
        .find(
          (eachFile) => eachFile.documentType === identityProofTypeForIndia
        );
      setIdentityProof(newIdentityProofFile);
    }
  }, [identityProofTypeForIndia]);

  return (
    <form
      className="flex flex-col max-w-[600px] w-full"
      onSubmit={handleSubmit(onSubmit, (err) => {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Please enter all the required details.',
        });
        setIsAddingDirector(false);
      })}
    >
      <DialogHeader>
        <DialogTitle>
          {director
            ? 'Edit director'
            : modalConfig?.heading || 'Add a new director'}
        </DialogTitle>
      </DialogHeader>
      <div className="grow overflow-y-auto max-h-[calc(85vh-172px)]">
        <div className="flex flex-col gap-6 h-full p-6">
          <div className="flex flex-col gap-6">
            <FieldWrapper>
              <Label className="text-sm gap-0.5 text-neutral-1">
                <span>Legal Name (as on your passport)</span>
                <span>*</span>
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="First name"
                  {...register('directorFirstName')}
                  error={errors?.directorFirstName?.message}
                  defaultValue={watch('directorFirstName')}
                />
                <Input
                  placeholder="Last name"
                  {...register('directorLastName')}
                  error={errors?.directorLastName?.message}
                  defaultValue={watch('directorLastName')}
                />
              </div>
            </FieldWrapper>
            <div className="grid grid-cols-2 gap-4">
              <FieldWrapper>
                <Label className="text-sm text-neutral-1">
                  Phone number
                  <span className="text-text-violet-tertiary ml-1">*</span>
                </Label>

                <PhoneInput
                  id="phoneNumber"
                  placeholder=""
                  className="h-[42px]"
                  {...register('number')}
                  onChange={(e) => {
                    setValue('number', e);
                    clearErrors('number');
                  }}
                  value={watch('number')}
                  error={errors?.number?.message}
                />
              </FieldWrapper>

              <FieldWrapper>
                <Label className="text-sm text-neutral-1">
                  <div className="flex items-center gap-2">
                    Email <span className="text-violet-solid ml-1">*</span>
                    <Tooltip
                      side="top"
                      content="Email must be unique across all entities, different from all company emails and other directors' emails."
                    >
                      <InfoToolTip />
                    </Tooltip>
                  </div>
                </Label>

                <Input
                  type="email"
                  placeholder="Email"
                  {...register('email')}
                  error={errors?.email?.message}
                  value={watch('email')}
                />
              </FieldWrapper>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FieldWrapper>
                <Label className="text-sm text-neutral-1">
                  Educational Qualification
                  <span className="text-violet-solid ml-1">*</span>
                </Label>
                <Select
                  {...register('personalDetails.educationalQualification')}
                  defaultValue={getValues(
                    'personalDetails.educationalQualification'
                  )}
                  onValueChange={(val) => {
                    setValue(
                      'personalDetails.educationalQualification',
                      val as EducationalQualificationEnum
                    );
                  }}
                >
                  <SelectTrigger
                    error={
                      !watch('personalDetails.educationalQualification') &&
                      errors?.personalDetails?.educationalQualification?.message
                        ? errors?.personalDetails?.educationalQualification
                            ?.message
                        : null
                    }
                  >
                    <SelectValue placeholder="Select education" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(
                      EDUCATIONAL_QUALIFICATION_TYPE_ENUM_TO_TEXT
                    ).map((val) => {
                      return (
                        <SelectItem key={val} value={val}>
                          {EDUCATIONAL_QUALIFICATION_TYPE_ENUM_TO_TEXT[val]}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {!watch('personalDetails.educationalQualification') &&
                errors?.personalDetails?.educationalQualification?.message ? (
                  <InputErrorMessage
                    message={
                      errors?.personalDetails?.educationalQualification?.message
                    }
                  />
                ) : null}
              </FieldWrapper>
              <FieldWrapper>
                <Label className="text-sm text-neutral-1">
                  Nationality<span className="text-violet-solid ml-1">*</span>
                </Label>

                <CountryInput
                  placeholder="Search country"
                  type="single"
                  onValueChange={(ctr) => {
                    setValue('personalDetails.nationality', ctr);
                    setIdentityProofTypeForIndia(undefined);
                    setIdentityProof(undefined);
                  }}
                  {...register('personalDetails.nationality')}
                  error={
                    errors?.personalDetails?.nationality?.message &&
                    !watch('personalDetails.nationality')
                      ? errors?.personalDetails?.nationality?.message
                      : undefined
                  }
                  value={watch('personalDetails.nationality')}
                  _className="overflow-hidden"
                />
              </FieldWrapper>
            </div>
          </div>

          <FieldWrapper>
            <Label>
              <span>Occupation</span>
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            <Input
              type="text"
              {...register('personalDetails.occupation')}
              placeholder="Occupation"
              error={errors?.personalDetails?.occupation?.message}
              value={watch('personalDetails.occupation')}
            />
          </FieldWrapper>

          <FieldWrapper>
            <Label>
              <span>Place of Birth</span>
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            <div className="flex flex-col gap-3">
              <div>
                <CountryInput
                  type="single"
                  value={watch('personalDetails.placeOfBirth.country')}
                  onValueChange={(val) => {
                    setValue('personalDetails.placeOfBirth.country', val);
                    clearErrors('personalDetails.placeOfBirth.country');
                  }}
                  error={
                    errors?.personalDetails?.placeOfBirth?.country?.message
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col w-full gap-2">
                  <Select
                    value={watch('personalDetails.placeOfBirth.state')}
                    onValueChange={(value) => {
                      setValue('personalDetails.placeOfBirth.state', value);
                      clearErrors('personalDetails.placeOfBirth.state');
                    }}
                  >
                    <SelectTrigger
                      error={
                        errors?.personalDetails?.placeOfBirth?.state?.message
                      }
                    >
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[240px]">
                      {getStatesDropdown(
                        watch('personalDetails.placeOfBirth.country') as Country
                      ).map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors?.personalDetails?.placeOfBirth?.state?.message && (
                    <InputErrorMessage
                      message={
                        errors?.personalDetails?.placeOfBirth?.state?.message
                      }
                    />
                  )}
                </div>
                <Input
                  placeholder="Enter district"
                  className="h-[45px]"
                  type="text"
                  {...register('personalDetails.placeOfBirth.district')}
                  error={
                    errors?.personalDetails?.placeOfBirth?.district?.message
                  }
                  value={watch('personalDetails.placeOfBirth.district')}
                />
              </div>
            </div>
          </FieldWrapper>

          <FieldWrapper>
            <Label>
              Current Address <span className="text-violet-solid ml-1">*</span>
            </Label>
            <AddressInput
              className="gap-3"
              address={watch('operatingAddress') as IAddress}
              onChange={(ad) => {
                setValue('operatingAddress', ad);
                if (ad.country !== watch('operatingAddress.country')) {
                  clearErrors('operatingAddress.state');
                }
              }}
              lastRowClassName="grid-cols-2 gap-4"
              error={{
                country:
                  errors?.operatingAddress?.country?.message &&
                  !watch('operatingAddress.country')
                    ? errors?.operatingAddress?.country?.message
                    : undefined,
                addressLine1:
                  !watch('operatingAddress.addressLine1') &&
                  errors?.operatingAddress?.addressLine1?.message
                    ? errors?.operatingAddress?.addressLine1?.message
                    : null,
                addressLine2:
                  !watch('operatingAddress.addressLine2') &&
                  errors?.operatingAddress?.addressLine2?.message
                    ? errors?.operatingAddress?.addressLine2?.message
                    : null,
                city:
                  !watch('operatingAddress.city') &&
                  errors?.operatingAddress?.city?.message
                    ? errors?.operatingAddress?.city?.message
                    : null,
                postalCode:
                  !watch('operatingAddress.postalCode') &&
                  errors?.operatingAddress?.postalCode?.message
                    ? errors?.operatingAddress?.postalCode?.message
                    : null,
                state:
                  !watch('operatingAddress.state') &&
                  errors?.operatingAddress?.state?.message
                    ? errors?.operatingAddress?.state?.message
                    : undefined,
              }}
            />
          </FieldWrapper>

          <FieldWrapper>
            <Label>
              <span>Date of Occupancy in Current Address</span>
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            <DatePicker
              hideCalendar
              date={watch('personalDetails.dateOfOccupancyInCurrentAddress')}
              setDate={(dt) => {
                clearErrors('personalDetails.dateOfOccupancyInCurrentAddress');
                setValue('personalDetails.dateOfOccupancyInCurrentAddress', dt);
              }}
              _triggerClassName="min-w-fit w-full"
              hasError={
                !!errors?.personalDetails?.dateOfOccupancyInCurrentAddress
                  ?.message
              }
              {...register('personalDetails.dateOfOccupancyInCurrentAddress')}
              disabled={{
                after: new Date(),
              }}
            />
            {errors?.personalDetails?.dateOfOccupancyInCurrentAddress
              ?.message ? (
              <InputErrorMessage
                message={
                  errors?.personalDetails?.dateOfOccupancyInCurrentAddress
                    ?.message
                }
              />
            ) : null}
          </FieldWrapper>

          {!watch('personalDetails.nationality') ||
          watch('personalDetails.nationality') !== Country.IN ? (
            <FieldWrapper>
              <Label>
                Upload Passport
                <span className="text-violet-solid ml-1">*</span>
              </Label>
              {watch('personalDetails.nationality') ? (
                <Alert
                  variant="info"
                  icon={
                    <Icons.InfoCircle
                      className={cn(
                        iconVariants({ variant: 'info' }),
                        'size-4'
                      )}
                    />
                  }
                  description="Passport should be notorized and apostilled in the country of issue."
                  descriptionClassName="txt-small-regular"
                  _className="p-3 my-2"
                />
              ) : null}
              <SingleFileUploader
                companyId={companyId}
                setFile={setIdentityProof}
                file={identityProof}
                error={errors?.identityProof?.message}
                setError={(msg: string) =>
                  setError('identityProof', { type: 'custom', message: msg })
                }
                token={token}
                accept={{
                  'application/pdf': ['.pdf'],
                  'image/*': ['.png', '.jpg', '.jpeg'],
                }}
                maxSize={10 * 1024 * 1024}
                documentType={LegacyDocumentType.Passport}
                _className="w-full"
              />
              {errors?.identityProof?.message ? (
                <InputErrorMessage message="Please upload your identity proof." />
              ) : null}
            </FieldWrapper>
          ) : (
            <FieldWrapper _className="flex flex-col gap-3">
              <Label>
                Upload Identity Proof
                <span className="text-violet-solid ml-1">*</span>
              </Label>
              <Select
                value={identityProofTypeForIndia}
                onValueChange={(val: LegacyDocumentType) =>
                  setIdentityProofTypeForIndia(val)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {identityProofDocumentTypes.map((docType) => (
                    <SelectItem key={docType} value={docType}>
                      {documentName(docType)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <SingleFileUploader
                companyId={companyId}
                setFile={setIdentityProof}
                file={identityProof}
                error={errors?.identityProof?.message}
                setError={(msg: string) =>
                  setError('identityProof', {
                    type: 'custom',
                    message: msg,
                  })
                }
                token={token}
                accept={{
                  'application/pdf': ['.pdf'],
                  'image/*': ['.png', '.jpg', '.jpeg'],
                }}
                maxSize={10 * 1024 * 1024}
                documentType={identityProofTypeForIndia}
                disabled={!identityProofTypeForIndia}
                _className="w-full"
              />
              {errors?.identityProof?.message ? (
                <InputErrorMessage message="Please upload your identity proof." />
              ) : null}
            </FieldWrapper>
          )}

          <FieldWrapper>
            <Label>
              Self-Attested Copy of Address Proof
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            {watch('personalDetails.nationality') &&
            watch('personalDetails.nationality') !== Country.IN ? (
              <Alert
                variant="info"
                icon={
                  <Icons.InfoCircle
                    className={cn(iconVariants({ variant: 'info' }), 'size-4')}
                  />
                }
                description="Address proof should be notorized and apostilled in the country of issue."
                descriptionClassName="txt-small-regular"
                _className="p-3 my-2"
              />
            ) : null}
            <SingleFileUploader
              companyId={companyId}
              setFile={setAddressProof}
              file={
                watch('addressProof')?.fileUrl
                  ? (watch('addressProof') as FileBasic)
                  : null
              }
              error={errors?.addressProof?.message}
              setError={(msg: string) =>
                setError('addressProof', { type: 'custom', message: msg })
              }
              token={token}
              accept={{
                'application/pdf': ['.pdf'],
                'image/*': ['.png', '.jpg', '.jpeg'],
              }}
              maxSize={10 * 1024 * 1024}
              documentType={LegacyDocumentType.AddressProof}
              _className="w-full"
            />
            {errors?.addressProof?.message ? (
              <InputErrorMessage message="Please upload your address proof. Utility bill or bank statement not older than 2 months is expected." />
            ) : (
              <span className="txt-small-regular">
                Note:- Utility bill or bank statement not older than 2 months.
              </span>
            )}
          </FieldWrapper>

          {watch('personalDetails.nationality') === Country.IN ? (
            <>
              <FieldWrapper>
                <Label>
                  Self-Attested Aadhar Card
                  <span className="text-violet-solid ml-1">*</span>
                </Label>
                <SingleFileUploader
                  companyId={companyId}
                  setFile={setAadharCard}
                  file={
                    watch('aadharCard')?.fileUrl
                      ? (watch('aadharCard') as FileBasic)
                      : null
                  }
                  error={errors?.aadharCard?.message}
                  setError={(msg: string) =>
                    setError('aadharCard', { type: 'custom', message: msg })
                  }
                  token={token}
                  accept={{
                    'application/pdf': ['.pdf'],
                    'image/*': ['.png', '.jpg', '.jpeg'],
                  }}
                  maxSize={10 * 1024 * 1024}
                  documentType={LegacyDocumentType.AadharCard}
                  _className="w-full"
                />
                {errors?.aadharCard?.message ? (
                  <InputErrorMessage message="Please upload your aadhar card. Avoid digilocker copies." />
                ) : (
                  <span className="txt-small-regular">
                    Note:- Avoid digilocker copies.
                  </span>
                )}
              </FieldWrapper>

              <FieldWrapper>
                <Label>
                  Self-Attested PAN
                  <span className="text-violet-solid ml-1">*</span>
                </Label>
                <SingleFileUploader
                  companyId={companyId}
                  setFile={setPanCard}
                  file={
                    watch('panCard')?.fileUrl
                      ? (watch('panCard') as FileBasic)
                      : null
                  }
                  error={errors?.panCard?.message}
                  setError={(msg: string) =>
                    setError('panCard', { type: 'custom', message: msg })
                  }
                  token={token}
                  accept={{
                    'application/pdf': ['.pdf'],
                    'image/*': ['.png', '.jpg', '.jpeg'],
                  }}
                  maxSize={10 * 1024 * 1024}
                  documentType={LegacyDocumentType.PanCard}
                  _className="w-full"
                />
                {errors?.panCard?.message ? (
                  <InputErrorMessage message="Please upload your pan card. Avoid digilocker copies." />
                ) : (
                  <span className="txt-small-regular">
                    Note:- Avoid digilocker copies.
                  </span>
                )}
              </FieldWrapper>
            </>
          ) : null}

          <FieldWrapper>
            <Label>
              Passport Size Photo
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            <SingleFileUploader
              companyId={companyId}
              setFile={setPassportSizePhoto}
              file={
                watch('passportSizePhoto')?.fileUrl
                  ? (watch('passportSizePhoto') as FileBasic)
                  : null
              }
              error={errors?.passportSizePhoto?.message}
              setError={(msg: string) =>
                setError('passportSizePhoto', { type: 'custom', message: msg })
              }
              token={token}
              accept={{
                'application/pdf': ['.pdf'],
                'image/*': ['.png', '.jpg', '.jpeg'],
              }}
              maxSize={10 * 1024 * 1024}
              documentType={LegacyDocumentType.PassportSizePhoto}
              _className="w-full"
            />
            {errors?.passportSizePhoto?.message ? (
              <InputErrorMessage message="Please upload your passport size photo." />
            ) : null}
          </FieldWrapper>

          <FieldWrapper>
            <Label>
              Specimen Signature
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            <SingleFileUploader
              companyId={companyId}
              setFile={setSpecimenSignature}
              file={
                watch('specimenSignature')?.fileUrl
                  ? (watch('specimenSignature') as FileBasic)
                  : null
              }
              error={errors?.specimenSignature?.message}
              setError={(msg: string) =>
                setError('specimenSignature', { type: 'custom', message: msg })
              }
              token={token}
              accept={{
                'application/pdf': ['.pdf'],
                'image/*': ['.png', '.jpg', '.jpeg'],
              }}
              maxSize={10 * 1024 * 1024}
              documentType={LegacyDocumentType.SpecimenSignature}
              _className="w-full"
            />
            {errors?.specimenSignature?.message ? (
              <InputErrorMessage message="Please upload your specimen signature." />
            ) : null}
          </FieldWrapper>

          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="hasDSC"
                {...register('personalDetails.hasDSC')}
                checked={watch('personalDetails.hasDSC')}
                onCheckedChange={(val) => {
                  if (val) {
                    setValue('personalDetails.hasDSC', true);
                  } else {
                    setValue('personalDetails.hasDSC', false);
                  }
                }}
              />
              <Label htmlFor="hasDSC">
                This director has a Digital Signature Certificate.
              </Label>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="hasDIN"
                  checked={watch('hasDIN')}
                  onCheckedChange={(val: boolean) => setValue('hasDIN', val)}
                />

                <Label htmlFor="hasDIN">This director has a DIN.</Label>
              </div>

              {watch('hasDIN') ? (
                <Input
                  placeholder="Enter DIN number"
                  {...register('directorIdentificationNumber')}
                  error={errors?.directorIdentificationNumber?.message}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setOpen(false)}
        >
          <span>Cancel</span>
        </Button>
        <Button type="submit" variant="primary" disabled={isAddingDirector}>
          {isAddingDirector ? <LoadingIndicator size={32} /> : null}
          <span
            className={classNames(
              'flex items-center',
              isAddingDirector ? 'ml-2' : ''
            )}
          >
            Save
          </span>
        </Button>
      </DialogFooter>
    </form>
  );
};

export default DirectorForm;
